if (!customElements.get("trusted-worldwide-awards")) {
  class TrustedWorldwideAwards extends HTMLElement {
    connectedCallback() {
      this.counters = Array.from(this.querySelectorAll("[data-counter]"));
      this.numberFormatters = new Map();
      this.abortController = new AbortController();
      this.reducedMotionQuery = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.handleMotionPreference = this.handleMotionPreference.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);

      this.reducedMotionQuery.addEventListener(
        "change",
        this.handleMotionPreference,
        {
          signal: this.abortController.signal,
        },
      );

      if (
        this.reducedMotionQuery.matches ||
        !("IntersectionObserver" in window)
      ) {
        this.displayTargets();
        return;
      }

      this.resetCounters();
      this.observer = new IntersectionObserver(
        this.handleIntersection,
        {
          rootMargin: "0px 0px -15% 0px",
          threshold: 0.2,
        },
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.animationFrame);
    }

    handleMotionPreference(event) {
      if (!event.matches) return;
      cancelAnimationFrame(this.animationFrame);
      this.hasAnimated = false;
      this.displayTargets();
      this.observer?.disconnect();
    }

    handleIntersection(entries) {
      const entry = entries.find((candidate) => candidate.target === this);
      if (!entry) return;

      if (entry.isIntersecting) {
        this.animateCounters();
        return;
      }

      cancelAnimationFrame(this.animationFrame);
      this.hasAnimated = false;
      this.resetCounters();
    }

    displayTargets() {
      this.counters.forEach((counter) => {
        const formattedValue = this.formatNumber(
          this.getTarget(counter),
          this.getFractionDigits(counter),
        );
        if (counter.textContent !== formattedValue) {
          counter.textContent = formattedValue;
        }
      });
    }

    resetCounters() {
      this.counters.forEach((counter) => {
        counter.textContent = this.formatNumber(0);
      });
    }

    animateCounters() {
      if (this.hasAnimated || !this.counters.length) return;
      this.hasAnimated = true;
      const duration = Number(this.dataset.counterDuration) || 1000;
      const startTime = performance.now();

      this.counters.forEach((counter) => {
        counter.textContent = "0";
      });

      const update = (currentTime) => {
        const progress = Math.min(1, (currentTime - startTime) / duration);
        const easedProgress = 1 - Math.pow(1 - progress, 3);

        this.counters.forEach((counter) => {
          const fractionDigits = this.getFractionDigits(counter);
          const precision = 10 ** fractionDigits;
          const currentValue = Math.round(
            this.getTarget(counter) * easedProgress * precision,
          ) / precision;
          const formattedValue = this.formatNumber(currentValue, fractionDigits);
          if (counter.textContent !== formattedValue) {
            counter.textContent = formattedValue;
          }
        });

        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(update);
        }
      };

      this.animationFrame = requestAnimationFrame(update);
    }

    getTarget(counter) {
      return Math.max(0, Number(counter.dataset.count) || 0);
    }

    getFractionDigits(counter) {
      return (counter.dataset.count.split(".")[1] || "").length;
    }

    formatNumber(value, fractionDigits = 0) {
      const locale = document.documentElement.lang || "en";
      const formatterKey = `${locale}:${fractionDigits}`;
      if (!this.numberFormatters.has(formatterKey)) {
        this.numberFormatters.set(
          formatterKey,
          new Intl.NumberFormat(locale, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          }),
        );
      }
      return this.numberFormatters.get(formatterKey).format(value);
    }
  }

  customElements.define("trusted-worldwide-awards", TrustedWorldwideAwards);
}
